import { useState, useCallback } from 'react';
import { Shield, Loader2, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OUTCOMES = [
  { id: 'receiver_feels_respected', label: 'Feel respected' },
  { id: 'receiver_feels_calm', label: 'Feel calm' },
  { id: 'receiver_feels_informed', label: 'Feel informed' },
  { id: 'receiver_feels_helped', label: 'Feel helped' },
  { id: 'receiver_feels_motivated', label: 'Feel motivated' },
  { id: 'receiver_feels_valued', label: 'Feel valued' },
];

const RELATIONSHIPS = [
  { id: 'colleague', label: 'Colleague' }, { id: 'boss', label: 'Boss' },
  { id: 'client', label: 'Client' }, { id: 'investor', label: 'Investor' },
  { id: 'team', label: 'Team member' }, { id: 'friend', label: 'Friend' },
  { id: 'stranger', label: 'Stranger' },
];

const SAMPLES = [
  { label: 'Escalation email', text: "I can't believe this is STILL not resolved. If you don't fix this by EOD I will have to escalate this to management." },
  { label: 'Feedback to team', text: "The work you submitted was honestly not good enough. We need to redo the entire thing and I'm frustrated this keeps happening." },
  { label: 'Client follow-up', text: "We haven't heard back from you in weeks. Are you still interested or should we move on?" },
];

export default function PICheckPage() {
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState('receiver_feels_respected');
  const [relationship, setRelationship] = useState('colleague');
  const [loading, setLoading] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [rewrite, setRewrite] = useState(null);

  const handleAnalyze = useCallback(async () => {
    if (text.trim().length < 5 || loading) return;
    setLoading(true); setAnalysis(null); setRewrite(null);
    try {
      const res = await fetch(`${API}/api/ai/analyze`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) setAnalysis(await res.json());
      else throw new Error('Analysis failed');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [text, loading]);

  const handleRewrite = useCallback(async () => {
    if (!text.trim() || rewriting) return;
    setRewriting(true); setRewrite(null);
    try {
      const res = await fetch(`${API}/api/ai/rewrite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, receiver_outcome: outcome, relationship_context: relationship }),
      });
      if (res.ok) { const d = await res.json(); setRewrite(d.pi_rewrite || d.rewrite || ''); }
      else throw new Error('Rewrite failed');
    } catch (err) { toast.error(err.message); }
    finally { setRewriting(false); }
  }, [text, outcome, relationship, rewriting]);

  const piScore = analysis?.pi_score ?? null;
  const scoreColor = piScore >= 70 ? '#10B981' : piScore >= 40 ? '#F59E0B' : '#EF4444';

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="picheck-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">PI Check</h1>
        <p className="text-white/40 text-sm">Predict how your message will land before you send it</p>
      </div>

      {/* How to use */}
      <div className="bg-[#0E1830] border border-white/5 rounded-xl p-4">
        <details>
          <summary className="text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70">How to use PI Check</summary>
          <div className="mt-3 text-white/35 text-xs space-y-1 leading-relaxed">
            <p>1. Paste or type your message in the text area</p>
            <p>2. Click &ldquo;Analyse&rdquo; to get your PI Score (0&ndash;100) and conflict signals</p>
            <p>3. Choose your desired receiver outcome and relationship context</p>
            <p>4. Click &ldquo;Rewrite&rdquo; to get an AI-optimized version that achieves your desired outcome</p>
          </div>
        </details>
      </div>

      {/* Sample prompts */}
      <div className="flex flex-wrap gap-2">
        <span className="text-white/20 text-xs self-center">Try:</span>
        {SAMPLES.map(s => (
          <button key={s.label} onClick={() => { setText(s.text); setAnalysis(null); setRewrite(null); }}
            className="px-3 py-1 text-xs rounded-full border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
          <textarea value={text} onChange={e => { setText(e.target.value); setAnalysis(null); setRewrite(null); }}
            placeholder="Type or paste your message here..."
            className="w-full h-36 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-white/90 text-sm resize-none focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
            data-testid="check-input" />
          <Button onClick={handleAnalyze} disabled={loading || text.trim().length < 5}
            className="w-full shimmer-btn text-white rounded-full font-space font-bold border-0" data-testid="check-analyse-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analysing...</> : <><Shield className="w-4 h-4 mr-2" /> Analyse Message</>}
          </Button>

          {analysis && (
            <div className="pt-4 border-t border-white/[0.06] space-y-4">
              <div className="space-y-2">
                <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Desired outcome</span>
                <div className="flex flex-wrap gap-1.5">
                  {OUTCOMES.map(o => (
                    <button key={o.id} onClick={() => setOutcome(o.id)}
                      className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${outcome === o.id ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{o.label}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Relationship</span>
                <div className="flex flex-wrap gap-1.5">
                  {RELATIONSHIPS.map(r => (
                    <button key={r.id} onClick={() => setRelationship(r.id)}
                      className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${relationship === r.id ? 'bg-[#06B6D4] text-white border-[#06B6D4]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{r.label}</button>
                  ))}
                </div>
              </div>
              <Button onClick={handleRewrite} disabled={rewriting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold" data-testid="check-rewrite-btn">
                {rewriting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Rewriting...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Rewrite Message</>}
              </Button>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          {analysis ? (
            <div className="space-y-4" data-testid="check-result">
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <svg width="80" height="80" className="-rotate-90">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                    <circle cx="40" cy="40" r="34" fill="none" stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={213.6} strokeDashoffset={213.6 - (piScore / 100) * 213.6} />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Georgia, serif', color: scoreColor }}>{piScore}</span>
                    <span className="text-[8px] uppercase text-white/40 font-bold">PI Score</span>
                  </div>
                </div>
                <div>
                  <p className="text-white font-bold text-sm">{analysis.receiver_experience}</p>
                  <p className="text-white/30 text-xs mt-1">Conflict probability: {analysis.conflict_probability}%</p>
                </div>
              </div>

              {/* Factors */}
              <div className="space-y-2">
                {Object.entries(analysis.factors || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-white/30 text-xs w-20 capitalize">{key}</span>
                    <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4]" style={{ width: `${val}%` }} />
                    </div>
                    <span className="text-white/40 text-xs w-8 text-right">{val}%</span>
                  </div>
                ))}
              </div>

              {rewrite && (
                <div className="mt-4 p-4 bg-emerald-500/[0.04] border border-emerald-500/20 rounded-xl" data-testid="check-rewrite-result">
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider block mb-2">Rewritten Message</span>
                  <p className="text-white/80 text-sm leading-relaxed">{rewrite}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Shield className="w-10 h-10 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">Analyse a message to see its PI Score</p>
              <p className="text-white/15 text-xs mt-1">Predicts how the receiver will feel</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
